import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-extensibility/commerce-extensibility/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "convert-payload-field-values"
    }}>{`Convert payload field values`}</h1>
    <p>{`The payload of an event often includes field values that are not easily interpretable for third-party implementations. For example, a value might be stored in the Commerce database as an integer, but the external system stores the same information as a string. Alternatively, instead of transforming data types, you might want to change a Commerce-supplied string to a string defined in the external system. To address this issue, you can implement a converter, enabling custom values for any field in the payload.`}</p>
    <h2 {...{
      "id": "converter-definition"
    }}>{`Converter definition`}</h2>
    <p>{`Your converter class must implement `}<inlineCode parentName="p">{`FieldConverterInterface`}</inlineCode>{`. This interface contains the `}<inlineCode parentName="p">{`convert`}</inlineCode>{` method, which accepts `}<inlineCode parentName="p">{`mixed`}</inlineCode>{` and `}<inlineCode parentName="p">{`Event`}</inlineCode>{` arguments. The method returns a `}<inlineCode parentName="p">{`mixed`}</inlineCode>{` data type. You must create individual converter classes for each field when converting multiple fields within a payload. You can reuse a class to replace a specific field across multiple events.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`interface FieldConverterInterface
{
    /**
     * Converts a field value
     *
     * @param mixed $value
     * @param Event $event
     * @return mixed
     */
    public function convert(mixed $value, Event $event);
}
`}</code></pre>
    <p>{`As an example, the `}<inlineCode parentName="p">{`observer.catalog_product_save_after`}</inlineCode>{` event contains a top-level `}<inlineCode parentName="p">{`visibility`}</inlineCode>{` field, which must contain an integer value. Convert these values to strings that match values on the external system. The following table describes these values.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Commerce value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Converted value`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`NOT_VISIBLE_INDIVIDUALLY`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This product should not be displayed if it is part of a configurable product.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CATALOG_ONLY`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This product appears in catalog listings, but not in searches.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`SEARCH_ONLY`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This product appears in searches, but not catalog listings.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`CATALOG_AND_SEARCH`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`This product appears in catalog listings and searches. For most products, this is the default.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`In the following example, the `}<inlineCode parentName="p">{`TestConverterVisibility`}</inlineCode>{` converter class updates the value of the `}<inlineCode parentName="p">{`visibility`}</inlineCode>{` field to a string.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`<?php
/**
 * Copyright [first year code created] Adobe
 * All rights reserved.
 */
declare(strict_types=1);

namespace Magento\\AdobeCommerceEventsClient\\Event;

use Magento\\AdobeCommerceEventsClient\\Event\\Filter\\FieldConverterInterface;

class TestConverterVisibility implements FieldConverterInterface
{
    /**
     * Method used to convert field value
     *
     * @param mixed $value
     * @return mixed
     */
    public function convert(mixed $value, Event $event): mixed
    {
        return match ($value) {
            '1' => 'NOT_VISIBLE_INDIVIDUALLY',
            '2' => 'CATALOG_ONLY',
            '3' => 'SEARCH_ONLY',
            '4' => 'CATALOG_AND_SEARCH'
        };
    }
}
`}</code></pre>
    <p>{`The default payload for this event includes the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "event":{
      "data":{
         "value":{
            "visibility":"4",
        }
      }
   }
}
`}</code></pre>
    <p>{`The converter changes the payload to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "event":{
      "data":{
         "value":{
            "visibility":"CATALOG_AND_SEARCH",
        }
      }
   }
}
`}</code></pre>
    <h2 {...{
      "id": "register-the-converter"
    }}>{`Register the converter`}</h2>
    <p>{`You must configure a module's `}<inlineCode parentName="p">{`io_events.xml`}</inlineCode>{` or root `}<inlineCode parentName="p">{`app/etc/io_events.xml`}</inlineCode>{` file to update the required fields. You can also declare them in the system `}<inlineCode parentName="p">{`env.php`}</inlineCode>{` or `}<inlineCode parentName="p">{`config.php`}</inlineCode>{` files or add them when using the CLI to subscribe to an event.`}</p>
    <h3 {...{
      "id": "command-line"
    }}>{`Command line`}</h3>
    <p>{`The `}<a parentName="p" {...{
        "href": "commands.md#subscribe-to-an-event"
      }}><inlineCode parentName="a">{`bin/magento events:subscribe --fields`}</inlineCode>{` command`}</a>{` defines the fields and converters to include in the payload of a subscribed event. The example command adds the `}<inlineCode parentName="p">{`visibility`}</inlineCode>{` field and provides the path to the converter class. You can specify multiple fields in the same request.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`bin/magento events:subscribe observer.catalog_product_save_after --fields="store_id" --fields='{"name":"visibility", "converter": "Magento\\AdobeCommerceEventsClient\\Event\\TestConverterVisibility"}'\`
`}</code></pre>
    <h3 {...{
      "id": "configphp-file"
    }}><inlineCode parentName="h3">{`config.php`}</inlineCode>{` file`}</h3>
    <p>{`The following example `}<inlineCode parentName="p">{`config.php`}</inlineCode>{` is the equivalent of the example `}<inlineCode parentName="p">{`bin/magento events:subscribe`}</inlineCode>{` command in the `}<strong parentName="p">{`Command line`}</strong>{` example above.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-php"
      }}>{`'io_events' => [
        'observer.catalog_product_save_after' => [
            'fields' => [
                'store_id',
                [
                    'name' => 'visibility',
                    'converter' => 'Magento\\\\AdobeCommerceEventsClient\\\\Event\\\\TestConverterVisibility'
                ]
            ],
            'enabled' => 1
        ]
 ]       
`}</code></pre>
    <h3 {...{
      "id": "configure-an-io_eventsxml-file"
    }}>{`Configure an `}<inlineCode parentName="h3">{`io_events.xml`}</inlineCode>{` file`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`converter`}</inlineCode>{` attribute of the `}<inlineCode parentName="p">{`field`}</inlineCode>{` element defines the converter class that updates the event data field value for the specified event. Only one converter class can be defined per field.`}</p>
    <p>{`The following example updates the value of the field `}<inlineCode parentName="p">{`visibility`}</inlineCode>{` present in the `}<inlineCode parentName="p">{`observer.catalog_product_save_after`}</inlineCode>{` event payload using the `}<inlineCode parentName="p">{`TestConverterVisibility`}</inlineCode>{` converter class.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-xml"
      }}>{`<config xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="urn:magento:module-commerce-events-client/etc/io_events.xsd">
    <event name="observer.catalog_product_save_after" >
        <fields>
            <field name="visibility" converter="Magento\\AdobeCommerceEventsClient\\Event\\TestConverterVisibility"/>
        </fields>
    </event>
</config>
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      